import { AbstractControl, ValidationErrors } from '@angular/forms';
import { webEnums } from '@vectronic/openApi-customer';
import parsePhoneNumberFromString from 'libphonenumber-js/max';

export class VectronicValidators {
  private static readonly emailRegex = new RegExp(
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  );
  private static readonly phoneAllowedCharacters = new RegExp(/^[0-9+\-\s()]+$/);

  static email(control: AbstractControl): ValidationErrors | null {
    const value: string = control.value;
    if (!value) {
      return null;
    }
    return VectronicValidators.emailRegex.test(value) ? null : { email: true };
  }

  static phone(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }
    return VectronicValidators.validatePhone(control.value, false);
  }

  static mobilePhone(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }
    return VectronicValidators.validatePhone(control.value, true);
  }

  private static validatePhone(value: string, requireMobile: boolean): ValidationErrors | null {
    if (!VectronicValidators.phoneAllowedCharacters.test(value)) {
      return {
        [requireMobile ? 'mobilePhone' : 'phone']: {
          message: 'Allowed characters: digits, +, -, spaces, ()',
        },
      };
    }
    if (value.trim().charAt(0) !== '+') {
      return {
        [requireMobile ? 'mobilePhone' : 'phone']: {
          message: 'Country code is missing (must start with +)',
        },
      };
    }

    const phoneNumber = parsePhoneNumberFromString(value);

    if (!phoneNumber || !phoneNumber.isValid()) {
      return {
        [requireMobile ? 'mobilePhone' : 'phone']: {
          message: 'Invalid phone number',
        },
      };
    }
    if (requireMobile) {
      const numberType = phoneNumber.getType();
      if (!['MOBILE', 'FIXED_LINE_OR_MOBILE'].includes(numberType)) {
        return { mobilePhone: { message: 'Invalid mobile phone number' } };
      }
    }
    return null;
  }

  static getAddressType(address: string): webEnums.NotificationAddressType {
    if (address?.includes('@')) return 'EMAIL';
    const firstChar = address?.substring(0, 1);
    if (['+', '(', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(firstChar)) {
      return 'SMS';
    }
    return 'EMAIL';
  }

  static isValidEmail(value: string): boolean {
    return VectronicValidators.emailRegex.test(value);
  }

  static isValidPhone(value: string): boolean {
    const firstTwoDigits = value.substring(0, 2);
    const newValue = firstTwoDigits === '00' ? `+${value.slice(2)}` : value;
    const phoneNumber = parsePhoneNumberFromString(newValue);
    return phoneNumber?.isValid() ?? false;
  }
}

export function isPhoneNumberMobile(value: string): boolean {
  const phoneNumber = parsePhoneNumberFromString(value);
  if (!phoneNumber || !phoneNumber.isValid()) {
    return false;
  }
  const numberType = phoneNumber.getType();
  return ['MOBILE', 'FIXED_LINE_OR_MOBILE'].includes(numberType);
}
